import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { type PipeMotion } from './bird-game-engine';
import {
  BIRD_SIZE,
  PIPE_OVERSHOOT,
  PIPE_WIDTH,
  PLAY_BOTTOM_INSET,
  PLAY_TOP_INSET,
} from './bird-game.constants';
import { birdGameStore } from './bird-game.store';
import { type BirdGameController } from './use-bird-game';

const EDGE_FADE_HEIGHT = 90;
const TOP_FADE = PLAY_TOP_INSET + EDGE_FADE_HEIGHT;
const BOTTOM_FADE = PLAY_BOTTOM_INSET + EDGE_FADE_HEIGHT;
// fades the pipes and bird at the edges without painting a color over the
// themed background
const EDGE_MASK = `linear-gradient(to bottom, transparent 0, black ${TOP_FADE}px, black calc(100% - ${BOTTOM_FADE}px), transparent 100%)`;

interface BirdGameStageProps {
  controller: BirdGameController;
}

export const BirdGameStage = observer(function BirdGameStage({
  controller,
}: BirdGameStageProps) {
  const { motion: channels, wingAngle } = controller;
  const { phase, status } = birdGameStore;

  return (
    <div
      ref={controller.attachStage}
      tabIndex={0}
      role="application"
      aria-label="Flight Mode"
      className="relative flex-1 overflow-hidden outline-none cursor-pointer select-none"
      onPointerDown={controller.handleTap}
      onKeyDown={controller.handleKeyDown}
      onBlur={controller.pause}
    >
      <div className="absolute inset-0" style={{ maskImage: EDGE_MASK }}>
        {channels.pipes.map((pipe, index) => (
          <Pipe key={index} pipe={pipe} />
        ))}

        <motion.div
          className={cn(
            'absolute left-0 top-0 flex items-center justify-center',
            phase !== 'open' && 'invisible'
          )}
          style={{
            width: BIRD_SIZE,
            height: BIRD_SIZE,
            marginLeft: -BIRD_SIZE / 2,
            x: channels.birdX,
            y: channels.birdY,
            rotate: channels.birdRotation,
          }}
        >
          <AppLogo size={BIRD_SIZE} wingAngle={wingAngle} />
        </motion.div>
      </div>

      <span className="absolute inset-x-0 top-10 text-center font-serif text-4xl text-foreground pointer-events-none">
        {birdGameStore.score}
      </span>

      {status !== 'playing' && <StatusOverlay />}
    </div>
  );
});

function Pipe({ pipe }: { pipe: PipeMotion }) {
  return (
    <>
      <motion.div
        className="absolute left-0 rounded-[14px] bg-secondary-active"
        style={{
          width: PIPE_WIDTH,
          top: -PIPE_OVERSHOOT,
          height: pipe.topHeight,
          x: pipe.x,
        }}
      />
      <motion.div
        className="absolute left-0 rounded-[14px] bg-secondary-active"
        style={{
          width: PIPE_WIDTH,
          top: pipe.bottomTop,
          height: pipe.bottomHeight,
          x: pipe.x,
        }}
      />
    </>
  );
}

const StatusOverlay = observer(function StatusOverlay() {
  const { status, score, highScore, phase } = birdGameStore;
  if (phase !== 'open') return null;

  if (status === 'gameOver') {
    return (
      <YStack
        center
        className="absolute inset-0 justify-center gap-3 pointer-events-none px-6"
      >
        <span className="font-serif text-4xl text-foreground">Game over</span>
        <span className="text-sm text-foreground-subtle">
          Score {score} · Best {highScore}
        </span>
        <span className="text-sm text-foreground-muted">
          Click to play again
        </span>
      </YStack>
    );
  }

  return (
    <YStack
      center
      className="absolute inset-x-0 bottom-16 pointer-events-none px-6"
    >
      <span className="text-sm text-foreground-subtle">
        {status === 'paused'
          ? 'Paused — click to resume'
          : 'Click or press Space to fly'}
      </span>
    </YStack>
  );
});
